<svelte:options
  customElement={{
    tag: "oc-floating-focus-v1",
    /*                       */
    extend: window.__components.extend(),
  }}
/>

<script lang="ts">
  import { passive } from "svelte/legacy";
  import { repositionElement } from "./utils/floaterUtils";

  let floater = $state<HTMLElement>();
  let target: HTMLElement | undefined = $state(undefined);
  let borderRadius: string = $state("0");

  let isVisible: boolean = $state(false);

  let monitorElementPositionInterval: NodeJS.Timeout;

  /*                                                                              */
  const onBlur = (event: FocusEvent): void => {
    const blurTarget = event.composedPath()[0];
    if (blurTarget instanceof HTMLElement) {
      blurTarget.classList.remove("oc-floating-focus-v1-hide-outline");
    }
  };

  const handleFloaterState = () => {
    if (!target) return;

    repositionElement(floater!, target, borderRadius);
  };

  const onFocus = (event: FocusEvent): void => {
    const focusTarget = event.composedPath()[0] as HTMLElement;

    if (
      event
        .composedPath()
        .some(
          (element) =>
            element instanceof HTMLElement && element.dataset.ocFloatingFocusV1Target !== undefined,
        )
    ) {
      target = focusTarget;
      target.classList.add("oc-floating-focus-v1-hide-outline");
    } else {
      target = undefined;
    }

    if (event.target instanceof HTMLElement && event.target.dataset.ocFloatingFocusV1Radius) {
      borderRadius = event.target.dataset.ocFloatingFocusV1Radius;
    } else {
      borderRadius = window.getComputedStyle(focusTarget).borderRadius;
    }

    handleFloaterState();
  };

  const handleMouseDown = (): void => {
    if (!target) return;
    target.classList.remove("oc-floating-focus-v1-hide-outline");
    isVisible = false;
  };

  const handleKeyDown = (e: KeyboardEvent): void => {
    if (!["Tab", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) return;
    isVisible = true;
  };

  const handleScrollResize = (): void => {
    if (!target) return;
    requestAnimationFrame(() => {
      if (target !== undefined) {
        repositionElement(floater!, target, borderRadius);
      }
    });
  };

  $effect(() => {
    clearInterval(monitorElementPositionInterval);
    if (isVisible && target) {
      monitorElementPositionInterval = setInterval(() => {
        if (target !== undefined) {
          repositionElement(floater!, target, borderRadius);
        }
      }, 250);
    }
  });
</script>

<svelte:window onresizecapture={handleScrollResize} />
<svelte:body
  onmousedown={handleMouseDown}
  onkeydown={handleKeyDown}
  use:passive={["scroll", () => handleScrollResize]}
  onfocusincapture={onFocus}
  onblurcapture={onBlur}
/>
<div
  bind:this={floater}
  class="floating-focus"
  class:floating-focus--visible={isVisible && target}
></div>

<style lang="scss" global>
  @use "@otto-ec/design-tokens/component" as tokens;
  .floating-focus {
    border: 0 solid currentColor;
    border-radius: inherit;
    position: absolute;
    transform: translate(-50%, -50%);
    opacity: 0;
    will-change: top, left, width, height, border-width, border-radius;
    box-sizing: content-box;
    pointer-events: none;
    overflow: hidden;
    z-index: 9999999999; /*                                                        */
    outline: tokens.$oc-semantic-focus-outline-radius-12 solid
      tokens.$oc-semantic-focus-outline-color;
    outline-offset: tokens.$oc-semantic-focus-outline-offset;

    @media (prefers-reduced-motion: no-preference) {
      transition-property: left, top, width, height, border-width, border-radius;
      transition-duration: 0.1s, 0.1s, 0.1s, 0.1s, 0.1s, 0.1s;
      transition-timing-function: ease, ease, ease, ease, ease, ease;
    }

    &--visible {
      opacity: 1;
    }
  }
</style>
